import clsx from 'clsx';
import { truncateText } from '@/utils/truncateText';
import type { ProvidedJobPost } from '../hooks/useProvidedJobPosts';
import { useEffect, useMemo, useRef, useState } from 'react';
import usePdf from '../hooks/usePdf';
import TagSelectPopover from './TagSelectPopover';
import JobPostStatusBadge from './JobPostStatusBadge';
import {
  MdLink,
  MdDescription,
  MdOpenInNew,
  MdShare,
  MdMoreVert,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdApartment,
  MdWork,
  MdLocationOn,
  MdBadge,
} from 'react-icons/md';
import { ImDatabase } from 'react-icons/im';
import { Button } from '../../../../components/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import { Tag, type TagColor } from '../../../../components/Tag';
import type { CustomTagColor } from '../../custom_tags/hooks/useCustomTags';
import useDuplicateProvidedJobPost from '../api/useDuplicateProvidedJobPost';
import { useQueryClient } from '@tanstack/react-query';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import DuplicateConfirmDialog from './DuplicateConfirmDialog';
import { Link, useNavigate } from '@tanstack/react-router';
import MemoDisplay from './MemoDisplay';
import ShareContentDialog from './ShareContentDialog';
import Checkbox from '../../../../components/Checkbox';

interface Props extends ProvidedJobPost {
  created_at: string;
  isChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  onDelete?: () => Promise<void> | void;
  extraMenuItems?: React.ReactNode;
  // 「求人を複製」メニューを非表示にする（リスト詳細画面など、複製操作が文脈に合わない場面で利用）
  hideDuplicate?: boolean;
  // 「削除」メニューを非表示にする（onDeleteが配線されない画面で誤操作を防ぐ目的）
  hideDelete?: boolean;
}

export default function ProvidedJobPostCard({
  id,
  title,
  job_position_name,
  mandatory_requirement,
  status,
  company,
  salary_info,
  employment_type_text,
  working_location,
  memo,
  counter_cache: {
    count_of_suggest,
    count_of_document_screening,
    count_of_interview,
    count_of_offer,
    count_of_close,
  },
  tags,
  urls: { show: _show, open },
  outer_job_post,
  shared_job_post,
  created_at,
  is_available,
  is_possible_delete,
  isChecked = false,
  onCheckedChange,
  onDelete,
  extraMenuItems,
  hideDuplicate = false,
  hideDelete = false,
}: Props) {
  const [showRequirements, setShowRequirements] = useState(false);
  const [showRequirementsShowButton, setShowRequirementsShowButton] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [isSharePopoverOpen, setIsSharePopoverOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const requirements = mandatory_requirement;
  const { downloadPdf } = usePdf();
  const duplicateProvidedJobPost = useDuplicateProvidedJobPost();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const requirementsRef = useRef<HTMLParagraphElement>(null);
  const shareContent = useMemo(() => {
    return `${company?.name}\n${title}\n${open}`;
  }, [company?.name, title, open]);
  useEffect(() => {
    if (requirementsRef.current) {
      // スクロール可能な高さと実際の高さを比較して省略されているかを判定
      const isOverflowing =
        requirementsRef.current.scrollHeight > requirementsRef.current.clientHeight;
      setShowRequirementsShowButton(isOverflowing);
    }
  }, [requirements]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      if (onDelete) {
        await onDelete();
      }
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete job post:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const res = await duplicateProvidedJobPost.mutateAsync({ id });
      setIsDuplicateDialogOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['/agent/api/provided_job_posts'],
        exact: false,
      });
      if (res?.id) {
        navigate({
          to: '/agent/provided_job_posts/$providedJobPostsId',
          params: { providedJobPostsId: res.id.toString() },
        });
      }
    } catch (error) {
      console.error('Failed to duplicate job post:', error);
    }
  };

  const handleDuplicateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDuplicateDialogOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="flex min-h-[224px] gap-4 rounded bg-white p-8 shadow-layer1">
      {/* チェックボックス */}
      <div
        className="flex shrink-0 items-start pt-1"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Checkbox
          checked={isChecked}
          onChange={(checked) => {
            onCheckedChange?.(checked);
          }}
        />
      </div>
      {/* カードの内容 */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {/* 1段目: タイトル、ステータス、アクションボタン */}
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <Link
              to="/agent/provided_job_posts/$providedJobPostsId"
              params={{ providedJobPostsId: id.toString() }}
              className="block truncate text-xl font-bold text-inherit no-underline hover:text-inherit"
            >
              {title}
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <div className="shrink-0">
              <JobPostStatusBadge id={id} status={status} created_at={created_at} />
            </div>
            {/* 取り込み元ページへのリンク */}
            {outer_job_post && (
              <Button color="tertiary" size="sm" asChild>
                <a
                  href={outer_job_post.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1"
                >
                  {outer_job_post.job_source_display_name ||
                    outer_job_post.job_source_name ||
                    '取り込み元を見る'}
                  <MdOpenInNew className="size-icon-xxs" />
                </a>
              </Button>
            )}
            {/* url が null（jobmiru で host 未解決の経路など / #3010）のときはリンク非表示 */}
            {shared_job_post?.url && (
              <Button color="tertiary" size="sm" asChild>
                <a
                  href={shared_job_post.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1"
                >
                  {shared_job_post.provider_display_name || '取り込み元を見る'}
                  <MdOpenInNew className="size-icon-xxs" />
                </a>
              </Button>
            )}

            {/* 共有ボタン */}
            <Popover open={isSharePopoverOpen} onOpenChange={setIsSharePopoverOpen}>
              <PopoverTrigger asChild>
                <Button color="secondary" size="sm" iconOnly>
                  <MdShare className="size-icon-xxs" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[130px] rounded-sm p-0 shadow-layer3">
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSharePopoverOpen(false);
                      if (!is_available) {
                        alert('公開中の求人票のみ共有できます。公開してください');
                      } else {
                        setIsShareDialogOpen(true);
                      }
                    }}
                    className="flex h-xxl items-center gap-xs px-md py-xs text-body-sm text-main hover:bg-gray"
                  >
                    <MdLink className="size-icon-xxs" />
                    WEB共有
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSharePopoverOpen(false);
                      downloadPdf(id);
                    }}
                    className="flex h-xxl items-center gap-xs px-md py-xs text-body-sm text-main hover:bg-gray"
                  >
                    <MdDescription className="size-icon-xxs" />
                    PDF共有
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <ShareContentDialog
              title="求人票のURLをコピーする"
              shareText={shareContent}
              open={isShareDialogOpen}
              onOpenChange={setIsShareDialogOpen}
            />

            {/* 操作ボタン */}
            <Popover open={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
              <PopoverTrigger asChild>
                <Button color="secondary" size="sm" iconOnly>
                  <MdMoreVert className="size-icon-xxs" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[130px] rounded-sm p-0 shadow-layer3">
                <div className="flex flex-col">
                  {!hideDuplicate && (
                    <DuplicateConfirmDialog
                      title={title}
                      onConfirm={handleDuplicate}
                      isLoading={duplicateProvidedJobPost.isPending}
                      open={isDuplicateDialogOpen}
                      onOpenChange={setIsDuplicateDialogOpen}
                    >
                      <button
                        type="button"
                        onClick={handleDuplicateClick}
                        className="flex h-xxl items-center gap-xs px-md py-xs text-body-sm text-main hover:bg-gray"
                      >
                        求人を複製
                      </button>
                    </DuplicateConfirmDialog>
                  )}
                  {!hideDelete && (
                    <DeleteConfirmDialog
                      title={title}
                      onConfirm={handleDelete}
                      isLoading={isDeleting}
                      open={isDeleteDialogOpen}
                      onOpenChange={setIsDeleteDialogOpen}
                    >
                      {is_possible_delete && (
                        <button
                          type="button"
                          onClick={handleDeleteClick}
                          className="flex h-xxl items-center gap-xs px-md py-xs text-body-sm text-danger hover:bg-gray"
                        >
                          削除
                        </button>
                      )}
                    </DeleteConfirmDialog>
                  )}
                  {extraMenuItems}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* 2段目: 企業情報とカウンター */}
        <div className="flex items-start justify-between gap-1">
          <div className="flex flex-wrap gap-4">
            {company && (
              <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
                <MdApartment size={20} />
                <p className="text-xs">{company.name}</p>
              </div>
            )}
            {job_position_name && (
              <div className="flex min-w-0 items-center gap-2 border-r border-solid border-gray-300 pr-4">
                <MdWork size={20} className="shrink-0" />
                <p className="min-w-0 truncate text-xs" title={job_position_name}>
                  {truncateText(job_position_name)}
                </p>
              </div>
            )}
            {salary_info && (
              <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
                <ImDatabase size={16} />
                <p className="text-xs">{salary_info}</p>
              </div>
            )}
            {employment_type_text && (
              <div className="flex items-center gap-2 border-r border-solid border-gray-300 pr-4">
                <MdBadge size={20} />
                <p className="text-xs">{employment_type_text}</p>
              </div>
            )}
            {working_location && (
              <div className="flex items-center gap-2">
                <MdLocationOn size={20} />
                <p className="text-xs">{working_location}</p>
              </div>
            )}
            {memo && <MemoDisplay memo={memo} />}
          </div>
          <div className="flex items-center gap-4">
            <Counter count={count_of_suggest} label="提案" />
            <Counter count={count_of_document_screening} label="書類選考" />
            <Counter count={count_of_interview} label="選考中" />
            <Counter count={count_of_offer} label="内定" />
            <Counter count={count_of_close} label="内定承諾" />
          </div>
        </div>

        {/* 3段目: タグ */}
        <div className="flex gap-1">
          <div className="flex flex-wrap items-center gap-2">
            {tags.length !== 0 &&
              tags.map((tag) => (
                <Tag key={tag.id} color={tag.color_hex as TagColor} size="sm">
                  {tag.name}
                </Tag>
              ))}
            <TagSelectPopover
              id={id}
              tags={tags.map((tag) => ({
                id: tag.id,
                name: tag.name,
                colorHex: tag.color_hex as CustomTagColor,
              }))}
            />
          </div>
        </div>

        {/* 4段目: 必須要件 */}
        {requirements && (
          <div className="rounded border border-solid border-border bg-white p-4">
            <div className="flex items-start gap-md">
              <p className="text-heading-xxs font-bold whitespace-nowrap">必須要件</p>
              <div className="flex flex-col gap-2">
                <p
                  ref={requirementsRef}
                  className={`text-body-xs break-words whitespace-pre-wrap ${
                    !showRequirements ? 'line-clamp-3' : ''
                  }`}
                >
                  {requirements}
                </p>
                {showRequirementsShowButton && (
                  <div className="w-fit">
                    <Button
                      color="tertiary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowRequirements(!showRequirements);
                      }}
                    >
                      {showRequirements ? (
                        <>
                          <MdKeyboardArrowUp className="size-icon-xxs" />
                          閉じる
                        </>
                      ) : (
                        <>
                          <MdKeyboardArrowDown className="size-icon-xxs" />
                          要件をもっと見る
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Counter({ count, label }: { count: number; label: string }) {
  return (
    <div className="flex h-[68px] w-[80px] flex-col items-center justify-center gap-1 rounded border border-solid border-border bg-white">
      <p className={clsx('text-base font-bold', count === 0 && 'text-border')}>{count}</p>
      <p className="text-xs font-bold">{label}</p>
    </div>
  );
}
